import React, { useState } from "react";
import { X, AlertTriangle, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

/**
 * Confirmation modal for cancelling a gift_order. Creates a refund_request
 * record that the Finance team processes later. The contribution itself
 * stays as `completed` (historical revenue intact).
 */
export default function CancelGiftModal({ open, onClose, contribution, onCancelled }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!open || !contribution) return null;

  // The contribution row must have a `gift_order_id` field for the cancel to work.
  // Backend lookup: we cancel via gift_order_id. The AdminEventDetail page already
  // enriches each row with gift_order_id (via /admin/events/{id}).
  const giftOrderId = contribution.gift_order_id;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!reason.trim()) {
      setError("Indiquez le motif d'annulation.");
      return;
    }
    if (!giftOrderId) {
      setError("Aucun cadeau livrable n'est encore associé à cette contribution.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/admin/gift-orders/${giftOrderId}/cancel`,
        { reason: reason.trim() },
      );
      onCancelled?.(data);
      onClose();
      setReason("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Échec de l'annulation.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="cancel-gift-modal"
      className="fixed inset-0 z-[55] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl max-h-[92vh] overflow-y-auto sm:m-4 rounded-t-2xl">
        <div className="border-b border-[#F2E2EC] px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-red-600">
              Annuler le cadeau
            </p>
            <h2 className="font-display text-xl font-black text-[#2A0E3C]">
              {contribution.product_name || "Cadeau"}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="cancel-gift-close"
            className="w-9 h-9 rounded-full bg-[#FCE7F3] text-[#5C4664] hover:text-[#D81B8C] flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          {typeof contribution.estimated_refund === "number" && (
            <div
              data-testid="cancel-gift-refund-estimate"
              className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-3"
            >
              <Receipt className="w-5 h-5 text-emerald-700 shrink-0" />
              <div className="flex-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                  Estimation du remboursement
                </p>
                <p className="font-display text-2xl font-black text-emerald-800 leading-tight mt-0.5">
                  ≈ ${contribution.estimated_refund.toFixed(2)}
                </p>
                <p className="text-[11px] text-emerald-700 mt-1 leading-snug">
                  Calcul = part proratée du <strong>prix fournisseur</strong> (hors frais Milkado &amp; TVA).
                </p>
              </div>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Cette action est irréversible.</p>
              <p className="text-xs mt-1">
                Une <strong>fiche de remboursement</strong> sera créée au profit de{" "}
                <strong>{contribution.contributor_name || "—"}</strong>
                {contribution.contributor_phone ? ` (${contribution.contributor_phone})` : ""}
                . L&apos;équipe Finance la traitera.
              </p>
              <p className="text-[11px] mt-2 text-amber-900 bg-amber-100 rounded-md px-2 py-1.5 leading-snug">
                ⚠ <strong>Politique de remboursement</strong> : seul le <strong>prix fournisseur</strong>{" "}
                est remboursable (frais de service Milkado et TVA non remboursés).
                Le remboursement effectif reste <strong>conditionnel à la politique de retour du fournisseur</strong>.
              </p>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
              Motif d&apos;annulation <span className="text-red-600">*</span>
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="cancel-gift-reason"
              placeholder="Ex: rupture fournisseur sans alternative, demande explicite du client…"
              rows={3}
              className="mt-1 rounded-xl border-[#F2E2EC]"
              autoFocus
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <p data-testid="cancel-gift-error">{error}</p>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="cancel-gift-cancel"
              className="flex-1 rounded-full border-[#F2E2EC]"
            >
              Retour
            </Button>
            <Button
              type="submit"
              disabled={submitting || !giftOrderId}
              data-testid="cancel-gift-confirm"
              className="flex-1 rounded-full bg-red-600 hover:bg-red-700 text-white gap-2"
            >
              <Receipt className="w-4 h-4" />
              {submitting ? "Création…" : "Créer la fiche"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
